import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { uploadFileViaSignedUrl } from "@/lib/upload";

// Three-step upload (forms-specific endpoints). Sub-bucket 4 hoisted the
// in-line three-step coordination into a shared helper; sub-bucket 15
// relocated it to `@/lib/upload` and renamed to `uploadFileViaSignedUrl`.
// This hook stays a thin forms-side wrapper that wires endpoint config +
// cache invalidation.
//
// Failure semantics unchanged from the original sub-bucket 2 port — see
// `lib/upload.ts` for the contract.

export function useUploadFormAttachment(
  slug: string,
  submissionId: string | null,
) {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiClientError, File>({
    mutationFn: async (file) => {
      if (!submissionId) {
        throw new ApiClientError(
          0,
          "no_submission_id",
          "Cannot upload: missing submission id",
        );
      }
      return uploadFileViaSignedUrl(file, {
        initEndpoint: "/portal/forms/attachments/upload-init",
        finalizeEndpoint: "/portal/forms/attachments",
        buildFinalizeBody: ({ storageKey, file }) => ({
          submission_id: submissionId,
          storage_key: storageKey,
          file_name: file.name,
          content_type: file.type || "application/octet-stream",
          size_bytes: file.size,
        }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.portal.forms.detail(slug),
      });
      if (submissionId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.portal.forms.draft(submissionId),
        });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to upload attachment");
    },
  });
}
